import { Button, Typography } from '@breetometer/components';
import { useComponentVisibilityApi, useModalApi } from 'BreetHooks';
import { selectTourStep, setTourStep, useAppDispatch, useAppSelector } from 'BreetRedux';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';

import {
	COACHMARK_GAP,
	COACHMARK_WIDTH,
	type ITourStepConfig,
	SPOTLIGHT_PADDING,
	tourSteps,
	VIEWPORT_MARGIN,
} from './SpotlightTour.helpers';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const MOBILE_BREAKPOINT = 1280;

const SpotlightTour = () => {
	const step = useAppSelector(selectTourStep);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { closeModalDirect } = useModalApi();
	const { isMobile } = useComponentVisibilityApi(MOBILE_BREAKPOINT);

	const [rect, setRect] = useState<DOMRect | null>(null);
	const [coachHeight, setCoachHeight] = useState(0);

	const coachRef = useRef<HTMLDivElement>(null);
	const hasScrolledRef = useRef(false);

	const config = step ? tourSteps[step] : null;

	const defaultSelector = (configArg: ITourStepConfig) =>
		isMobile && configArg.mobileSelector ? configArg.mobileSelector : configArg.selector;
	const selector = config ? defaultSelector(config) : null;
	const placement = config && isMobile && config.mobilePlacement ? config.mobilePlacement : config?.placement;
	const description = config && isMobile && config.mobileDescription ? config.mobileDescription : config?.description;

	useLayoutEffect(() => {
		if (!config || !selector) {
			setRect(null);
			return;
		}

		hasScrolledRef.current = false;

		const measure = () => {
			const candidates = Array.from(document.querySelectorAll(selector));
			const el = candidates.find((node) => {
				const r = node.getBoundingClientRect();
				return r.width > 0 && r.height > 0;
			});
			if (el) {
				const r = el.getBoundingClientRect();
				const fullyVisible = r.top >= 0 && r.bottom <= window.innerHeight;
				if (!hasScrolledRef.current && !fullyVisible) {
					hasScrolledRef.current = true;
					el.scrollIntoView({ block: 'center', behavior: 'smooth' });
				}
				setRect(r);
			}
			return !!el;
		};

		let pollId: ReturnType<typeof setInterval> | undefined;
		if (!measure()) {
			let attempts = 0;
			pollId = setInterval(() => {
				attempts += 1;
				if (measure()) {
					clearInterval(pollId);
				} else if (attempts > 50) {
					clearInterval(pollId);
					setRect(null);
					dispatch(setTourStep(null));
				}
			}, 100);
		}

		window.addEventListener('resize', measure);
		window.addEventListener('scroll', measure, true);

		return () => {
			clearInterval(pollId);
			window.removeEventListener('resize', measure);
			window.removeEventListener('scroll', measure, true);
		};
	}, [config, selector, dispatch]);

	useLayoutEffect(() => {
		setCoachHeight(coachRef.current?.offsetHeight ?? 0);
	}, [config, rect]);

	const close = useCallback(() => dispatch(setTourStep(null)), [dispatch]);

	const handleCta = useCallback(() => {
		if (config?.ctaPath) {
			navigate(config.ctaPath)?.catch(console.error);
			closeModalDirect('onBoardingTodoList');
		}
		if (config?.nextStep) {
			dispatch(setTourStep(config.nextStep));
		} else {
			close();
		}
	}, [config, navigate, closeModalDirect, dispatch, close]);

	if (!config || !rect) return null;

	const isBottom = placement === 'bottom';

	const cutoutStyle = {
		top: rect.top - SPOTLIGHT_PADDING,
		left: rect.left - SPOTLIGHT_PADDING,
		width: rect.width + SPOTLIGHT_PADDING * 2,
		height: rect.height + SPOTLIGHT_PADDING * 2,
	};

	const coachWidth = Math.min(COACHMARK_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);

	const rawLeft = isBottom ? rect.left : rect.right + COACHMARK_GAP;
	const rawTop = isBottom ? rect.bottom + COACHMARK_GAP : rect.top;
	const left = clamp(rawLeft, VIEWPORT_MARGIN, window.innerWidth - coachWidth - VIEWPORT_MARGIN);
	const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - coachHeight - VIEWPORT_MARGIN);
	const top = clamp(rawTop, VIEWPORT_MARGIN, maxTop);

	const arrowStyle = isBottom
		? { left: clamp(rect.left + rect.width / 2 - left, 20, coachWidth - 20) }
		: { top: clamp(rect.top + rect.height / 2 - top, 20, 200) };

	const showSteps = !!config.step && !!config.steps;

	return createPortal(
		<div className='spotlight-tour'>
			<div
				className='spotlight-cutout'
				style={cutoutStyle}
			/>

			<div
				ref={coachRef}
				className={`spotlight-coachmark spotlight-coachmark--${placement}`}
				style={{ top, left, width: coachWidth }}
			>
				<span
					className='spotlight-arrow'
					style={arrowStyle}
				/>

				<Typography
					as='h6'
					className='spotlight-title color-tertiary-400'
				>
					{config.title}
				</Typography>
				<Typography className='spotlight-desc color-primary-50'>{description}</Typography>

				<div className='spotlight-actions'>
					{showSteps && (
						<span className='spotlight-step color-primary-200'>
							Step {config.step} of {config.steps}
						</span>
					)}

					<div className='spotlight-buttons'>
						<Button
							variant='native'
							className='spotlight-skip'
							onClick={close}
						>
							Skip
						</Button>
						<Button onClick={handleCta}>{config.ctaLabel}</Button>
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default SpotlightTour;
